export interface DashboardColumnsVisibility {
  documentNo?: boolean;
  storageType?: boolean;
  status?: boolean;
  poReference?: boolean;
  orderDate?: boolean;
  deliveryDate?: boolean;
  numberOfLines?: boolean;
  totalWeight?: boolean;
  totalCost?: boolean;
  actionEdit?: boolean;
  actionReorder?: boolean;
  actionCancel?: boolean;
  invoiceNo?: boolean;
  orderNo?: boolean;
  dueDate?: boolean;
  actionView?: boolean;
  [key: string]: boolean | undefined;
}

interface OrderFormFieldsVisibility {
  header?: Record<string, boolean>;
  lineColumns?: Record<string, boolean>;
  availableItems?: Record<string, boolean>;
}

interface DisplaySettings {
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  language: string;
  currency: string;
  pageSize: number;
  itemsPageSize: number;
  showVat: boolean;
  searchAllDefault: boolean;
  dashboardColumns: {
    openOrders: DashboardColumnsVisibility;
    postedOrders: DashboardColumnsVisibility;
  };
  orderFormFields: OrderFormFieldsVisibility;
}

interface BrandingSettings {
  tenantName: string;
  logoUrl: string;
  logoBase64: string;
  logoSource: string;
  primaryColor: string;
  secondaryColor: string;
}

interface OrderingSettings {
  requireApproval: boolean;
  approvalThreshold: number;
  maxOrderValue: number;
  defaultDeliveryDays: number;
  allowBackorders: boolean;
}

interface SecuritySettings {
  passwordMinLength: number;
  passwordRequireSpecialChar: boolean;
  passwordRequireNumber: boolean;
  passwordRequireUppercase: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  /** Two-step sign-in policy. 'future' is the legacy placeholder value and behaves like 'optional'. */
  mfa?: 'off' | 'optional' | 'future' | 'required';
  /** Who "required" applies to. */
  mfaScope?: 'all' | 'admins';
  /** Email users when they sign in from a new browser/device. Default on (undefined = on). */
  newDeviceAlerts?: boolean;
  /** ADMIN-REVIEW L3: email tenant admins about sensitive changes (default on). */
  adminAlerts?: boolean;
  /** L3 follow-up: email tenant admins when support (BC Portal or the partner) opens the portal (default on). */
  supportAccessAlerts?: boolean;
  /** A17: sign people out after this many idle minutes (0 / unset = off). */
  idleSignOutMinutes?: number;
}

export type EmailMode = 'off' | 'test' | 'on';
export type RegistrationMode = 'off' | 'approval' | 'auto';
export type DocMode = 'order' | 'quote';

/**
 * Derive the legacy salesQuotes / quoteWorkflow flags from the three independent
 * controls. These two booleans are kept in sync so existing consumers (sidebar,
 * dashboard, OrderForm) continue to work unchanged.
 *
 *   salesQuotes   = enableQuotes            (is the quote feature available at all)
 *   quoteWorkflow = (docMode === 'quote')   (does the New button default to a quote)
 *
 * docMode is only the default document type; enableQuotes is what actually turns
 * the quote feature on. Mirrors BC's Enable Quotes + Portal Document Mode.
 */
export const deriveQuoteFlags = (mode: DocMode, enableQuotes: boolean): { salesQuotes: boolean; quoteWorkflow: boolean } => {
  return {
    salesQuotes: enableQuotes,
    quoteWorkflow: mode === 'quote',
  };
};

interface FeaturesSettings {
  multiCompany: boolean;
  advancedReporting: boolean;
  apiIntegration: boolean;
  auditLogging: boolean;
  emailMode: EmailMode;
  emailTestRecipient: string;
  registrationMode: RegistrationMode;
  exportData: boolean;
  pdfDocuments: boolean;
  calculateDiscounts: boolean;
  paymentStatus: boolean;
  orderComments: boolean;
  invoices: boolean;
  shipments: boolean;
  creditMemos: boolean;
  statements: boolean;
  customFields: boolean;
  multiCustomerAccess: boolean;
  docMode: DocMode;
  enableOrders: boolean;
  enableQuotes: boolean;
  salesQuotes: boolean;
  quoteWorkflow: boolean;
  showInternalComments: boolean;
  showNonItemLines: boolean;
  shipmentMethodSelection: boolean;
  // Account self-service gates (default off) — let customers edit their own
  // company details / contacts / ship-to addresses from My Account.
  accountEditCompany: boolean;
  accountEditContacts: boolean;
  accountEditShipTo: boolean;
}

export interface AllSettings {
  branding: BrandingSettings;
  display: DisplaySettings;
  ordering: OrderingSettings;
  security: SecuritySettings;
  features: FeaturesSettings;
}

interface ColumnDefinition {
  key: string;
  label: string;
  required?: boolean;
  isAction?: boolean;
}

interface FieldDefinition {
  key: string;
  label: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export const inputClass = 'w-full max-w-[400px] p-2.5 border border-input rounded-sm text-sm';
export const labelClass = 'block text-sm font-semibold mb-1.5';
export const hintClass = 'text-xs text-content-secondary mt-1';
export const sectionTitle = 'm-0 mb-5 text-lg font-semibold';

export const getDefaultSettings = (): AllSettings => ({
  branding: { tenantName: '', logoUrl: '', logoBase64: '', logoSource: 'none', primaryColor: '#2196F3', secondaryColor: '#1976D2' },
  display: {
    timezone: 'Europe/London',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    language: 'en-GB',
    currency: 'GBP',
    pageSize: 20,
    itemsPageSize: 50,
    showVat: true,
    searchAllDefault: false,
    dashboardColumns: {
      openOrders: {
        documentNo: true, storageType: true, status: true, poReference: true,
        orderDate: true, deliveryDate: true, numberOfLines: true,
        totalWeight: true, totalCost: true, actionEdit: true, actionReorder: true,
        actionCancel: false
      },
      postedOrders: {
        invoiceNo: true, storageType: true, status: true, orderNo: true,
        poReference: true, orderDate: true, dueDate: true, numberOfLines: true,
        totalWeight: true, totalCost: true, actionView: true, actionReorder: true
      }
    },
    orderFormFields: {
      header: { storageType: true, shipTo: true, billTo: true, customer: true },
      lineColumns: { storageType: true, weight: true, units: true, unitPrice: true, totalCost: true, comments: true },
      availableItems: { showFilters: true, storageTypeFilter: true }
    }
  },
  ordering: { requireApproval: false, approvalThreshold: 1000, maxOrderValue: 50000, defaultDeliveryDays: 7, allowBackorders: true },
  security: { passwordMinLength: 8, passwordRequireSpecialChar: true, passwordRequireNumber: true, passwordRequireUppercase: true, sessionTimeout: 30, maxLoginAttempts: 5 },
  features: {
    multiCompany: false, advancedReporting: true, apiIntegration: true,
    auditLogging: true, emailMode: 'on', emailTestRecipient: '', registrationMode: 'approval',
    exportData: true, pdfDocuments: true, calculateDiscounts: true, paymentStatus: true,
    orderComments: true, invoices: true, shipments: true, creditMemos: true, statements: true,
    customFields: true, multiCustomerAccess: false,
    docMode: 'order', enableOrders: true, enableQuotes: false, salesQuotes: false, quoteWorkflow: false,
    showInternalComments: true, showNonItemLines: false,
    shipmentMethodSelection: false,
    accountEditCompany: false, accountEditContacts: false, accountEditShipTo: false
  }
});

export const openOrderColumns: ColumnDefinition[] = [
  { key: 'documentNo', label: 'Order No', required: true },
  { key: 'storageType', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'poReference', label: 'PO Reference' },
  { key: 'orderDate', label: 'Order Date' },
  { key: 'deliveryDate', label: 'Delivery Date' },
  { key: 'numberOfLines', label: 'Lines' },
  { key: 'totalWeight', label: 'KGs' },
  { key: 'totalCost', label: 'Amount' },
  { key: 'amountIncludingVAT', label: 'Amount (inc. VAT)' },
  { key: 'actionEdit', label: 'Edit/View Button', isAction: true },
  { key: 'actionReorder', label: 'Reorder Button', isAction: true },
  { key: 'actionCancel', label: 'Cancel Button', isAction: true },
];


export const postedOrderColumns: ColumnDefinition[] = [
  { key: 'invoiceNo', label: 'Invoice No', required: true },
  { key: 'storageType', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'orderNo', label: 'Order No' },
  { key: 'poReference', label: 'PO Reference' },
  { key: 'orderDate', label: 'Order Date' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'numberOfLines', label: 'Lines' },
  { key: 'totalWeight', label: 'KGs' },
  { key: 'totalCost', label: 'Amount' },
  { key: 'amountIncludingVAT', label: 'Amount (inc. VAT)' },
  { key: 'actionView', label: 'View Button', isAction: true },
  { key: 'actionReorder', label: 'Reorder Button', isAction: true },
];

export const orderFormHeaderFields: FieldDefinition[] = [
  { key: 'storageType', label: 'Item Storage Type' },
  { key: 'shipTo', label: 'Ship-To Block' },
  { key: 'customer', label: 'Customer Block' },
  { key: 'billTo', label: 'Bill-To Block' },
  { key: 'vat', label: 'VAT Details' },
];

export const orderFormLineColumns: FieldDefinition[] = [
  { key: 'storageType', label: 'Storage Type' },
  { key: 'weight', label: 'Weight (KG)' },
  { key: 'units', label: 'Units (UoM)' },
  { key: 'unitPrice', label: 'Unit Price' },
  { key: 'totalCost', label: 'Line Amount' },
  { key: 'comments', label: 'Comments' },
];

export const orderFormAvailableItems: FieldDefinition[] = [
  { key: 'showFilters', label: 'Show Item Filters' },
  { key: 'storageTypeFilter', label: 'Storage Type Filter' },
];

export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = { 'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': '$' };
  return symbols[currencyCode] || '';
};
